import React from "react";
import { Search, MessageCircle, CheckCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const HowNomaroofWorks = () => {
  return (
    <div className="relative py-16 lg:py-24 overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-orange-100/30 to-pink-100/30 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-orange-100/30 to-pink-100/30 rounded-full blur-3xl -z-10"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-50 to-pink-50 rounded-full border border-orange-200 mb-6">
            <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-sm font-semibold text-orange-700">Easy & Fast</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Your Home Abroad in{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-600 via-orange-500 to-pink-500">
              3 Simple Steps
            </span>
          </h2>

          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            From browsing to moving in, we have streamlined everything. No complicated paperwork, no hidden fees, no stress.
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-orange-200 via-orange-300 to-pink-200 rounded-full -z-10"></div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-6">
            {/* Step 1 - Orange */}
            <div className="relative group">
              <div className="relative bg-white dark:bg-neutral-800 rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-orange-200 dark:hover:border-orange-700 h-full flex flex-col">
                <div className="absolute -top-4 -left-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                    <span className="text-white text-xl font-bold">1</span>
                  </div>
                </div>

                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100 dark:opacity-20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Search className="w-8 h-8 text-orange-500" strokeWidth={2.5} />
                </div>

                <div className="flex-1">
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    Discover Your Perfect Space
                  </h3>
                  <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                    Browse our curated collection of student-friendly apartments, shared houses, and private rooms.
                  </p>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-orange-600 rounded-b-2xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>

              <div className="hidden lg:block absolute top-12 -right-10 z-10">
                <ArrowRight className="w-8 h-8 text-orange-300" />
              </div>
            </div>

            {/* Step 2 - Pink/Orange gradient */}
            <div className="relative group">
              <div className="relative bg-white dark:bg-neutral-800 rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-orange-200 dark:hover:border-orange-700 h-full flex flex-col">
                <div className="absolute -top-4 -left-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center shadow-lg">
                    <span className="text-white text-xl font-bold">2</span>
                  </div>
                </div>

                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-50 to-pink-100 dark:opacity-20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <MessageCircle className="w-8 h-8 text-orange-500" strokeWidth={2.5} />
                </div>

                <div className="flex-1">
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    Connect & Request
                  </h3>
                  <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                    Send a booking request instantly. Our hosts will respond right away
                  </p>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-pink-500 rounded-b-2xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>

              <div className="hidden lg:block absolute top-12 -right-10 z-10">
                <ArrowRight className="w-8 h-8 text-orange-300" />
              </div>
            </div>

            {/* Step 3 - Pink */}
            <div className="relative group">
              <div className="relative bg-white dark:bg-neutral-800 rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-orange-200 dark:hover:border-orange-700 h-full flex flex-col">
                <div className="absolute -top-4 -left-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center shadow-lg">
                    <span className="text-white text-xl font-bold">3</span>
                  </div>
                </div>

                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-50 to-pink-100 dark:opacity-20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <CheckCircle className="w-8 h-8 text-pink-500" strokeWidth={2.5} />
                </div>

                <div className="flex-1">
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    Move In With Confidence
                  </h3>
                  <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                    Secure your room by paying your first month’s rent within 36 hours — and you’re all set!
                  </p>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-500 to-pink-600 rounded-b-2xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-12 lg:mt-16">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4">
            <Link
              to="/listing-stay-map"
              className="group relative px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <span className="relative z-10 flex items-center gap-2">
                Browse Available Rooms
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
        </div>

        <div className="mt-12 lg:mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8 max-w-4xl mx-auto">
          <div className="text-center p-4 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-neutral-800 dark:to-neutral-700 hover:scale-105 transition-transform duration-300">
            <div className="text-2xl lg:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-600 to-pink-600 mb-1">
              2hrs
            </div>
            <div className="text-xs lg:text-sm text-gray-600 dark:text-gray-400 font-medium">
              Average Host Response 
            </div>
          </div>

          <div className="text-center p-4 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-neutral-800 dark:to-neutral-700 hover:scale-105 transition-transform duration-300">
            <div className="text-2xl lg:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-600 to-pink-600 mb-1">
              36hrs
            </div>
            <div className="text-xs lg:text-sm text-gray-600 dark:text-gray-400 font-medium">
              To Secure Room
            </div>
          </div>

          <div className="text-center p-4 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-neutral-800 dark:to-neutral-700 hover:scale-105 transition-transform duration-300">
            <div className="text-2xl lg:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-600 to-pink-600 mb-1">
              500+
            </div>
            <div className="text-xs lg:text-sm text-gray-600 dark:text-gray-400 font-medium">
              Happy Students
            </div>
          </div>

          <div className="text-center p-4 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-neutral-800 dark:to-neutral-700 hover:scale-105 transition-transform duration-300">
            <div className="text-2xl lg:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-600 to-pink-600 mb-1">
              24/7
            </div>
            <div className="text-xs lg:text-sm text-gray-600 dark:text-gray-400 font-medium">
              Local Support
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowNomaroofWorks;